import { type DepositAccount } from '@fruitful/income-split';
import { InputNumber } from 'primereact/inputnumber';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Label,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../../common/components/Toasts';
import useSimulateIncomeSplit, {
    type SimulateIncomeSplitResponse,
} from '../../hooks/useSimulateIncomeSplit';
import { IncomeSplitSimulationResults } from './IncomeSplitSimulationResults';

interface IncomeSplitSimulateModalProps {
    isOpen: boolean;
    onClose: () => void;
    accounts: DepositAccount[];
}

export const IncomeSplitSimulateModal = ({
    isOpen,
    onClose,
    accounts,
}: IncomeSplitSimulateModalProps) => {
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [amountCents, setAmountCents] = useState<number>(0);
    const [simulationResult, setSimulationResult] =
        useState<SimulateIncomeSplitResponse | null>(null);
    const [validationErrors, setValidationErrors] = useState<{
        accountId?: string;
        amountCents?: string;
    }>({});

    const toast = useToastController();
    const { mutateAsync: simulateIncomeSplit, isPending: isSimulating } =
        useSimulateIncomeSplit();

    // Filter to only internal accounts (Synctera accounts) - memoize to prevent unnecessary re-renders
    const internalAccounts = useMemo(
        () => accounts.filter((account) => !account.isExternal),
        [accounts],
    );

    // Track previous isOpen state to only reset when modal first opens
    const prevIsOpenRef = useRef(isOpen);

    useEffect(() => {
        // Only reset when modal transitions from closed to open
        if (isOpen && !prevIsOpenRef.current) {
            // Reset form when modal opens
            setSelectedAccountId('');
            setAmountCents(0);
            setSimulationResult(null);
            setValidationErrors({});
            // Set default to first account if available
            if (internalAccounts.length > 0 && internalAccounts[0]) {
                setSelectedAccountId(internalAccounts[0].id);
            }
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, internalAccounts]);

    const handleSimulate = async () => {
        console.log('handleSimulate called', {
            selectedAccountId,
            amountCents,
        });

        const errors: typeof validationErrors = {};

        if (!selectedAccountId) {
            errors.accountId = 'Please select an account';
        }

        if (amountCents <= 0) {
            errors.amountCents = 'Amount must be greater than 0';
        }

        if (Object.keys(errors).length > 0) {
            console.log('Validation errors:', errors);
            setValidationErrors(errors);
            return;
        }

        try {
            console.log('Calling simulateIncomeSplit with:', {
                syncteraAccountId: selectedAccountId,
                amountCents,
            });
            const result = await simulateIncomeSplit({
                body: {
                    syncteraAccountId: selectedAccountId,
                    amountCents,
                },
            });

            console.log('Simulation result:', result);
            setSimulationResult(result);
            toast.show(
                'Simulation completed successfully',
                MutationSuccessToastOptions,
            );
        } catch (error) {
            console.error('Simulation error:', error);
            toast.show(
                'Failed to simulate income split',
                MutationErrorToastOptions(error),
            );
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose}>
            <YStack
                gap={20}
                width={600}
                padding={20}
                overflow="scroll"
                maxHeight="80vh"
            >
                <BodyLargeEmphasized textAlign="center">
                    Simulate Income Split
                </BodyLargeEmphasized>

                {internalAccounts.length === 0 ? (
                    <BodyMediumRegular textAlign="center" color="red">
                        No internal Synctera accounts available for this
                        membership.
                    </BodyMediumRegular>
                ) : (
                    <>
                        <YStack gap={12}>
                            <YStack>
                                <Label htmlFor="account">Landing Account</Label>
                                <select
                                    id="account"
                                    value={selectedAccountId}
                                    onChange={(e) => {
                                        setSelectedAccountId(e.target.value);
                                        setValidationErrors((prev) => ({
                                            ...prev,
                                            accountId: undefined,
                                        }));
                                    }}
                                    disabled={isSimulating}
                                    style={{
                                        padding: 12,
                                        borderRadius: 8,
                                        borderWidth: 1,
                                        borderColor: '#ccc',
                                        fontSize: 16,
                                        width: '100%',
                                    }}
                                >
                                    <option value="">
                                        Select an account...
                                    </option>
                                    {internalAccounts.map((account) => (
                                        <option
                                            key={account.id}
                                            value={account.id}
                                        >
                                            {account.nickname} ({account.id})
                                        </option>
                                    ))}
                                </select>
                                {validationErrors.accountId != null &&
                                    validationErrors.accountId !== '' && (
                                        <BodyMediumRegular color="red">
                                            {validationErrors.accountId}
                                        </BodyMediumRegular>
                                    )}
                            </YStack>

                            <YStack>
                                <Label htmlFor="amount">Amount (cents)</Label>
                                <InputNumber
                                    key={`amount-input-${isOpen}`}
                                    id="amount"
                                    disabled={isSimulating}
                                    value={amountCents}
                                    onValueChange={(e) => {
                                        const newValue =
                                            e.value != null
                                                ? Number(e.value)
                                                : 0;
                                        console.log(
                                            'InputNumber onValueChange',
                                            {
                                                eValue: e.value,
                                                eValueType: typeof e.value,
                                                newValue,
                                                currentAmountCents: amountCents,
                                            },
                                        );
                                        setAmountCents(newValue);
                                        setValidationErrors((prev) => ({
                                            ...prev,
                                            amountCents: undefined,
                                        }));
                                    }}
                                    min={0}
                                    maxFractionDigits={0}
                                    useGrouping={false}
                                    style={{ width: '100%' }}
                                />
                                {validationErrors.amountCents != null &&
                                    validationErrors.amountCents !== '' && (
                                        <BodyMediumRegular color="red">
                                            {validationErrors.amountCents}
                                        </BodyMediumRegular>
                                    )}
                                <BodyMediumRegular
                                    color="$gray1000"
                                    fontSize={12}
                                >
                                    Enter amount in cents (e.g., 100000 =
                                    $1,000.00)
                                </BodyMediumRegular>
                            </YStack>
                        </YStack>

                        {simulationResult?.plan != null && (
                            <YStack gap={16}>
                                <BodyLargeEmphasized>
                                    Simulation Result:
                                </BodyLargeEmphasized>
                                <IncomeSplitSimulationResults
                                    result={simulationResult.plan}
                                    fullResponse={simulationResult}
                                />
                            </YStack>
                        )}

                        {isSimulating && (
                            <XStack justifyContent="center">
                                <Spinner />
                            </XStack>
                        )}

                        {!isSimulating && (
                            <XStack gap={12}>
                                {simulationResult ? (
                                    <>
                                        <Button
                                            flex={1}
                                            variant={ButtonVariant.SECONDARY}
                                            size={ButtonSize.LARGE}
                                            text="Run New Simulation"
                                            onPress={() => {
                                                setSimulationResult(null);
                                                setAmountCents(0);
                                            }}
                                        />
                                        <Button
                                            flex={1}
                                            variant={ButtonVariant.PRIMARY}
                                            size={ButtonSize.LARGE}
                                            text="Close"
                                            onPress={onClose}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            flex={1}
                                            variant={ButtonVariant.SECONDARY}
                                            size={ButtonSize.LARGE}
                                            text="Close"
                                            onPress={onClose}
                                        />
                                        <Button
                                            width="auto"
                                            variant={ButtonVariant.PRIMARY}
                                            size={ButtonSize.LARGE}
                                            text="Simulate"
                                            onPress={() => {
                                                console.log('Button clicked', {
                                                    selectedAccountId,
                                                    amountCents,
                                                });
                                                void handleSimulate();
                                            }}
                                            disabled={
                                                !selectedAccountId ||
                                                amountCents <= 0
                                            }
                                        />
                                    </>
                                )}
                            </XStack>
                        )}
                    </>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};
